import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { ProjectsService } from '../../../../../../../core/projects.service';
import { CreateOrEditDeliveryTrackingComponent } from '../create-or-edit-delivery-tracking/create-or-edit-delivery-tracking.component';
import { Field } from '../../../../../../../../../shared/models';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../../shared/components';
import * as fromRoot from '../../../../../../../../../state/app.state';
import {
  FetchDeliveryTrackingFieldsRequest,
  DeleteDeliveryTrackingFieldRequest,
} from '../../../../../../../core/projects.actions';
import { deliveryTrackingFieldsListInfo } from '../../../../../../../core/projects.selectors';

@Component({
    selector: 'app-delivery-tracking-list',
    templateUrl: './delivery-tracking-list.component.html',
    styleUrls: ['./delivery-tracking-list.component.scss'],
    standalone: false
})
export class DeliveryTrackingListComponent implements OnInit, OnDestroy {
  projectId: string;

  displayedColumns: string[] = ['label', 'name', 'type', 'uom', 'actions'];
  dataSource: Field[] = [];
  filteredDataSource: Field[] = [];
  originalDataSource: Field[] = [];
  selectedRow: Field | null = null;
  totalRecords = 0;
  pagination = {
    pageIndex: 0,
    pageSize: 10,
  };
  currentPage = 1;
  itemsPerPage = 10;

  typeLabelsMap: Record<string, string> = {
    number: 'Number',
    string: 'String',
    textarea: 'Textarea',
    date: 'Date',
    time: 'Time',
    boolean: 'Boolean',
    json: 'JSON',
  };

  private readonly onDestroy = new Subject<void>();

  constructor(
    private store: Store<fromRoot.State>,
    private projectsService: ProjectsService,
    private route: ActivatedRoute,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    if (this.route.snapshot.paramMap.has('projectId')) {
      this.projectId = this.route.snapshot.paramMap.get('projectId');
      this.fetchDeliveryTrackingFields();
    }

    this.store
      .select(deliveryTrackingFieldsListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          data = JSON.parse(JSON.stringify(data));
          this.currentPage = 1;

          if (data && data?.list?.length > 0) {
            this.originalDataSource = data?.list;
            this.filteredDataSource = [...this.originalDataSource];
            this.totalRecords = data.totalRecords || this.originalDataSource.length;
            this.paginateData(true);
          } else {
            this.originalDataSource = [];
            this.filteredDataSource = [];
            this.totalRecords = 0;
            this.dataSource = [];
          }
        }
      });
  }

  fetchDeliveryTrackingFields(): void {
    const payload = {
      projectId: this.projectId,
    };

    if (this.projectId) {
      this.store.dispatch(FetchDeliveryTrackingFieldsRequest({ payload }));
    }
  }

  rowClicked(row: Field): void {
    this.selectedRow = row;

    this.projectsService.openRightPanel.next({
      component: CreateOrEditDeliveryTrackingComponent,
      data: {
        ...row,
        projectId: this.projectId,
      },
    });
  }

  create(): void {
    this.projectsService.openRightPanel.next({
      component: CreateOrEditDeliveryTrackingComponent,
      data: {
        projectId: this.projectId,
      },
    });
  }

  onPageChanged(page: number): void {
    this.currentPage = page;
    this.pagination.pageIndex = page - 1;
    this.paginateData();
  }

  paginateData(reset = false): void {
    if (reset) {
      this.currentPage = 1;
    }
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, this.filteredDataSource.length);

    this.dataSource = this.filteredDataSource.slice(startIndex, endIndex);
  }

  deleteField(row: Field, event: Event): void {
    event.stopPropagation();
    const label = row?.label || 'this field';
    const message = `Deleting <b>${label}</b> cannot be undone.<br />Proceed to delete?`;
    const dialogData = new ConfirmDialogModel('Delete?', message);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const payload = {
          projectId: this.projectId,
          field: row,
        };
        this.store.dispatch(DeleteDeliveryTrackingFieldRequest({ payload }));
      }
    });
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
